/**
 * 공유 스냅숏(`ShareBrandPayload`) 하나를 상세 머리·개요·광고·상품 카드가 받는 모양으로 편다.
 *
 * 🔑 공유 페이지(`brand-hub-share-view`)와 가이드 체험 화면이 같은 함수를 쓴다 — 접는 함수는 실화면 것 그대로다
 *   (`readBrandHubHead` · `toBrandHubTagRows` · `readBrandHubPeerRanking` · `toAdMediaCard` …).
 * ⚠️ 'use client' 가 없다 — 서버 쪽에서 import 해도 깨지지 않게 둔다.
 */

import type { ShareBrandPayload } from 'src/types/hub/hub-share';

import { dedupeCards, toAdMediaCard, toProductMediaCard } from 'src/sections/search-all/utils/search-all-media-cards';

import { toBrandHubRank, brandHubRankLabel } from './brand-hub-rank';
import {
  readBrandHubHead,
  readBrandHubCount,
  toBrandHubTagRows,
  readBrandHubChannels,
  readBrandHubPeerRanking,
  brandHubPeerSelfRankShown,
} from './brand-hub-overview-rows';

/**
 * 상세 머리 · 얼굴 · 카드 — 표기 고르개와 무관한 값.
 * @param now 광고 집행 일수·영상 주소 만료를 셀 기준 시각 — 비면 지금(`Date.now()`)
 */
export function toBrandHubShareDisplay(data: ShareBrandPayload, now?: number) {
  const { mentions } = readBrandHubHead(data.head);
  const trend = data.pivots.trend ?? null;
  const flagged = new Map(data.flagged);
  return {
    head: {
      loading: false,
      total: mentions,
      recent7d: readBrandHubCount(data.week),
      prev7d: readBrandHubCount(data.prevWeek),
      topChannel: readBrandHubChannels(trend).topChannel,
      adTotal: data.ads?.total ?? 0,
      productTotal: data.products?.total ?? 0,
      uniqueMentions: data.uniqueMentions,
    },
    faces: new Map(data.faces),
    /** 순위에서 뺄 브랜드 — 서버가 상세와 같은 판정으로 실어 왔다 */
    flagged,
    /**
     * 이름 옆 「N위」 — 목록 기본 정렬(언급 순)의 `#` 과 같은 수.
     * ⚠️ 제품군이 걸리면 비운다 — 목록은 그때 짝 맞춤 질문(`ranking.categoryBrands`)으로 세는데 스냅숏의 `peers` 는 다른 질문이다.
     */
    rankLabel: data.cond.categoryTerms.length > 0 ? '' : brandHubRankLabel(toBrandHubRank(data.brand, data.peers?.brands ?? null, flagged)),
    profiles: new Map(data.profiles),
    adCards: dedupeCards((data.ads?.items ?? []).map((ad) => toAdMediaCard(ad, now))),
    productCards: dedupeCards((data.products?.items ?? []).map(toProductMediaCard)),
  };
}

export type BrandHubShareDisplay = ReturnType<typeof toBrandHubShareDisplay>;

/**
 * 개요 면이 받는 값 — 성분·피부 고민은 고른 표기(표준 · 원문)로.
 * @param view `toBrandHubShareDisplay` 의 값(얼굴 · 뺀 브랜드)
 * @param raw 원문 표기를 고른 칸
 */
export function toBrandHubShareOverview(data: ShareBrandPayload, view: BrandHubShareDisplay, raw: { ingredients: boolean; concerns: boolean }) {
  // 막대 끝 번호도 이름 옆 「N위」처럼 제품군이 걸리면 비운다(`brandHubPeerSelfRankShown`)
  const { peers, excluded } = readBrandHubPeerRanking(data.brand, data.peers, view.flagged, brandHubPeerSelfRankShown('mentions', data.cond.categoryTerms));
  return {
    dailyTable: data.pivots.daily ?? null,
    trend: data.pivots.trend ?? null,
    channelTable: data.pivots.trend ?? null,
    marketTable: data.pivots.markets ?? null,
    coBrands: toBrandHubTagRows(data.pivots.coBrands, { skip: data.brand }),
    hashtags: toBrandHubTagRows(data.pivots.hashtags, { hash: true }),
    ingredients: toBrandHubTagRows(raw.ingredients ? data.pivots.ingredientsRaw : data.pivots.ingredients),
    concerns: toBrandHubTagRows(raw.concerns ? data.pivots.concernsRaw : data.pivots.concerns),
    accounts: data.accounts,
    adStats: data.adStats,
    adTotal: data.ads?.total ?? 0,
    products: data.products?.items ?? [],
    productTotal: data.products?.total ?? 0,
    productFacets: data.products?.facets ?? null,
    peers,
    peersExcluded: excluded,
    faces: view.faces,
    railLoading: false,
    headLoading: false,
    peersLoading: false,
  };
}
